import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type Props = { workspace: Workspace };

/**
 * Per-workspace toggle that hides the Composer's Codex dock tab (Framework /
 * Voice / Avatars) for users who don't want that surface. Data is preserved
 * when off — re-enabling restores the editing surface unchanged.
 *
 * See mem://features/knowledge-worker-codex-toggle.md.
 */
export const KnowledgeWorkerCodexCard = ({ workspace }: Props) => {
  const { refresh } = useWorkspace();
  const [saving, setSaving] = useState(false);
  const enabled = workspace.codex_enabled !== false;

  const onToggle = async (next: boolean) => {
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ codex_enabled: next })
      .eq("id", workspace.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(next ? "Codex enabled for this workspace" : "Codex hidden for this workspace");
    void refresh();
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Knowledge Worker Codex</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="codex-toggle" className="text-sm font-medium">
              Show Codex in this workspace's Composer
            </Label>
            <p className="text-xs text-muted-foreground">
              The Codex is the home for your Topic Tree, Voice profile, and Audience Avatars in the Composer's bottom dock. Turn it off to keep the Composer focused on writing and publishing without the deeper planning surfaces.
            </p>
          </div>
          <Switch
            id="codex-toggle"
            checked={enabled}
            disabled={saving}
            onCheckedChange={onToggle}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Builder, Vault, and Corpus stay visible regardless of this setting. Your existing voice profile, avatars, and topic tree are preserved while the tab is hidden — re-enable any time to keep editing them.
        </p>
      </CardContent>
    </Card>
  );
};
